import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Phone,
  Mic,
  Brain,
  BarChart3,
  Shield,
  Users,
  Zap,
  Target,
  CheckCircle,
  ArrowRight,
  Play,
  Star,
  TrendingUp,
  MessageSquare,
  GraduationCap,
  Award,
  Clock,
  FileText,
  Bell,
  Layers,
  ChevronRight,
  Sparkles,
  Menu,
  X,
  Headphones,
  Building2,
  Globe,
  Heart,
  Briefcase,
  HeadphonesIcon,
  UserCheck,
  LineChart,
  AudioWaveform
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Insyra AI Logo URL
const INSYRA_LOGO = "/insyra-logo.png";

// Core Features - Highlighting the platform capabilities
const coreFeatures = [
  {
    icon: AudioWaveform,
    title: 'AI Transcription',
    description: 'Automatic speech-to-text with intelligent speaker diarization and emotion detection',
    gradient: 'from-violet-500 to-purple-600',
    bgColor: 'bg-violet-50',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600'
  },
  {
    icon: TrendingUp,
    title: 'Quality Scoring',
    description: '10+ dimension analysis scoring every call against your custom quality parameters',
    gradient: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-50',
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600'
  },
  {
    icon: Shield,
    title: 'Compliance Monitoring',
    description: 'Real-time EEOC violation detection and policy compliance alerts',
    gradient: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600'
  },
  {
    icon: Brain,
    title: 'AI Coaching',
    description: 'Personalized coaching recommendations powered by conversation intelligence',
    gradient: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-50',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600'
  }
];

// Platform capabilities
const capabilities = [
  { icon: Phone, text: 'OneDrive Auto-Sync', desc: 'Automatic call ingestion from cloud storage' },
  { icon: BarChart3, text: 'Analytics Dashboard', desc: 'Real-time performance metrics and trends' },
  { icon: MessageSquare, text: 'QA Notes & Reviews', desc: 'Structured feedback and review workflows' },
  { icon: Target, text: 'KPI Management', desc: 'Custom scoring parameters per tenant' },
  { icon: FileText, text: 'Case Studies', desc: 'Best practice call library for training' },
  { icon: Users, text: 'Team Collaboration', desc: 'Multi-level user roles and permissions' },
  { icon: Clock, text: 'Audit Trails', desc: 'Complete activity logging and compliance' },
  { icon: Award, text: 'Leaderboards', desc: 'Gamified performance rankings' }
];

// Stats
const stats = [
  { value: '10+', label: 'Quality Dimensions', sublabel: 'analyzed per call' },
  { value: '95%', label: 'Time Saved', sublabel: 'on manual QA' },
  { value: '100%', label: 'Compliance', sublabel: 'monitoring coverage' },
  { value: '24/7', label: 'AI Analysis', sublabel: 'always working' }
];

// Testimonial highlights
const testimonialHighlights = [
  'Automated quality audits that previously took hours',
  'Identified compliance risks before they became issues',
  'Improved team performance scores by 40%',
  'Reduced manual review time by 95%'
];

// Industry use cases
const industries = [
  { icon: Briefcase, name: 'Staffing & RPO', description: 'Recruitment calls, candidate screening', color: 'blue' },
  { icon: HeadphonesIcon, name: 'BPO & Call Centers', description: 'Customer service and support', color: 'purple' },
  { icon: Building2, name: 'KPO Services', description: 'Knowledge services and consulting', color: 'emerald' },
  { icon: Globe, name: 'Sales Teams', description: 'Outbound sales and lead qualification', color: 'amber' }
];

export default function NewLandingPage() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    toast.success('Welcome to Insyra AI!');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Navigation */}
      <nav className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled ? "bg-white/90 backdrop-blur-lg shadow-sm" : "bg-transparent"
      )}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={INSYRA_LOGO} alt="Insyra AI" className="h-12 w-auto" />
              <span className="text-xl font-bold text-slate-900">INSYRA AI</span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">Features</a>
              <a href="#capabilities" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">Capabilities</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">How It Works</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">Pricing</a>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-slate-700 hover:text-slate-900"
              >
                Sign In
              </Button>
              <Button 
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg shadow-violet-500/25"
              >
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-slate-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-slate-200 pt-4">
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-slate-600 hover:text-slate-900">Features</a>
                <a href="#capabilities" className="text-slate-600 hover:text-slate-900">Capabilities</a>
                <a href="#how-it-works" className="text-slate-600 hover:text-slate-900">How It Works</a>
                <a href="#pricing" className="text-slate-600 hover:text-slate-900">Pricing</a>
                <Link to="/login" className="text-slate-600 hover:text-slate-900">Sign In</Link>
                <Button onClick={handleGetStarted} className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white w-full">
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-violet-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob" />
          <div className="absolute top-40 right-10 w-72 h-72 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000" />
          <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-4000" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <Badge className="mb-6 bg-violet-100 text-violet-700 border-violet-200 hover:bg-violet-100 px-4 py-1.5">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              AI-Powered Call Quality Platform
            </Badge>

            {/* Logo Display */}
            <div className="flex justify-center items-center gap-4 mb-8">
              <img src={INSYRA_LOGO} alt="Insyra AI" className="h-24 md:h-32 w-auto" />
              <span className="text-4xl md:text-5xl font-bold text-slate-900">INSYRA AI</span>
            </div>

            {/* Main Heading - Tagline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 mb-6">
              Voice
              <span className="mx-2 md:mx-3 text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-cyan-500">→</span>
              Insight
              <span className="mx-2 md:mx-3 text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-emerald-500">→</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-violet-600">Impact</span>
            </h1>

            {/* Subheading */}
            <p className="text-xl md:text-2xl text-slate-600 leading-relaxed mb-10 max-w-3xl mx-auto">
              Transform every conversation into actionable intelligence. 
              <span className="text-slate-900 font-medium"> AI-powered analysis, real-time coaching, </span>
              and comprehensive compliance monitoring for recruitment excellence.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Button 
                size="lg"
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-xl shadow-violet-500/30 h-14 px-8 text-lg"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-100 h-14 px-8 text-lg"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </Button>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center p-4 bg-white/60 rounded-2xl backdrop-blur-sm border border-slate-200/50">
                  <p className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-indigo-600">
                    {stat.value}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 mt-1">{stat.label}</p>
                  <p className="text-xs text-slate-500">{stat.sublabel}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-200">Core Features</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Everything you need for <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-cyan-500">call quality excellence</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive AI-powered tools that automate quality assurance while delivering personalized coaching for every team member.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {coreFeatures.map((feature, idx) => (
              <div 
                key={idx}
                className="group relative bg-white rounded-2xl p-8 border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className={cn("inline-flex items-center justify-center w-14 h-14 rounded-xl mb-6", feature.iconBg)}>
                  <feature.icon className={cn("h-7 w-7", feature.iconColor)} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 text-lg leading-relaxed">{feature.description}</p>
                <div className="absolute top-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="h-6 w-6 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section id="capabilities" className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200">Full Platform</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Built for recruitment teams
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From automated call ingestion to detailed analytics, everything you need in one platform.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {capabilities.map((cap, idx) => (
              <div 
                key={idx}
                className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-slate-100 group-hover:bg-violet-100 mb-4 transition-colors">
                  <cap.icon className="h-6 w-6 text-slate-600 group-hover:text-violet-600 transition-colors" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-1">{cap.text}</h4>
                <p className="text-sm text-slate-500">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">Industries</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Built for every customer-facing team
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {industries.map((industry, idx) => (
              <div key={idx} className="bg-slate-50 rounded-xl p-6 text-center hover:shadow-lg transition-all hover:bg-white border border-transparent hover:border-slate-200">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-white shadow-sm mb-4">
                  <industry.icon className="h-7 w-7 text-violet-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-1">{industry.name}</h4>
                <p className="text-sm text-slate-500">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-violet-100 text-violet-700 border-violet-200">How It Works</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Three steps to transform your QA
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                step: '01',
                title: 'Connect & Sync',
                description: 'Connect your OneDrive or upload calls directly. Insyra AI automatically transcribes and processes every conversation.',
                color: 'violet'
              },
              {
                step: '02',
                title: 'Analyze & Score',
                description: 'AI analyzes each call across 10+ quality dimensions, detecting compliance issues and identifying coaching opportunities.',
                color: 'cyan'
              },
              {
                step: '03',
                title: 'Coach & Improve',
                description: 'Team members receive personalized coaching insights, track progress on dashboards, and continuously improve.',
                color: 'emerald'
              }
            ].map((item, idx) => (
              <div key={idx} className="relative text-center">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-${item.color}-500 to-${item.color}-600 text-white text-2xl font-bold mb-6 shadow-lg shadow-${item.color}-500/30`}>
                  {item.step}
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Highlights */}
      <section className="py-24 bg-gradient-to-r from-violet-600 to-indigo-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Trusted by recruitment teams worldwide
            </h2>
            <p className="text-violet-200 text-lg">
              See what Insyra AI can do for your organization
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonialHighlights.map((highlight, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <CheckCircle className="h-8 w-8 text-emerald-400 mb-4" />
                <p className="text-white font-medium">{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-200">Pricing</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-slate-600">Start free, scale as you grow</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Starter */}
            <Card className="bg-white border-slate-200 shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-slate-900">Starter</CardTitle>
                <CardDescription>Perfect for trying Insyra AI</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">$0</span>
                  <span className="text-slate-500">/month</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {['50 calls/month', '5 users', 'AI Transcription', 'Basic Scoring', 'Email Support'].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button className="w-full mt-6" variant="outline" onClick={handleGetStarted}>Get Started</Button>
              </CardContent>
            </Card>
            
            {/* Professional */}
            <Card className="bg-gradient-to-b from-violet-50 to-white border-violet-200 shadow-lg relative scale-105">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-violet-600 text-white">Most Popular</Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-slate-900">Professional</CardTitle>
                <CardDescription>For growing teams</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">$99</span>
                  <span className="text-slate-500">/month</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {['500 calls/month', '25 users', 'Custom Call Types', 'QA Notes & Notifications', 'AI Coaching', 'Audit Trail', 'Priority Support'].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button className="w-full mt-6 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700" onClick={handleGetStarted}>Get Started</Button>
              </CardContent>
            </Card>
            
            {/* Enterprise */}
            <Card className="bg-white border-slate-200 shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-slate-900">Enterprise</CardTitle>
                <CardDescription>For large organizations</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">Custom</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {['Unlimited calls', 'Unlimited users', 'White-label branding', 'Custom integrations', 'API access', 'Dedicated CSM', 'SLA guarantee'].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button className="w-full mt-6" variant="outline">Contact Sales</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-6 w-6 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Ready to transform your <br />call quality process?
          </h2>
          <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
            Join forward-thinking recruitment teams using Insyra AI to automate QA, ensure compliance, and develop their people.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-xl shadow-violet-500/30 h-14 px-10 text-lg"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-slate-300 text-slate-700 hover:bg-slate-100 h-14 px-10 text-lg"
            >
              Sign In to Your Account
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={INSYRA_LOGO} alt="Insyra AI" className="h-8 w-auto brightness-0 invert" />
              <span className="text-white font-bold">INSYRA AI</span>
              <span className="text-slate-400 text-sm">| Voice → Insight → Impact</span>
            </div>
            <div className="flex gap-8 text-slate-400 text-sm">
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <a href="#capabilities" className="hover:text-white transition-colors">Capabilities</a>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
            </div>
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Insyra AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Custom Styles for Animations */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}
